import {
  Button,
  Grid,
  IconButton,
  Paper,
  Typography
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import { Formik } from "formik";
import _ from "lodash";
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Terminal from "terminal-in-react";
import { t } from "ttag";
import { useStoreActions, useStoreState } from "../../models/RootStore";
import AdvancedInputField from "../../widgets/AdvancedInputField/AdvancedInputField";
import SimpleModal from "../../widgets/SimpleModal/SimpleModal";
import ConfigComponent from "./ConfigComponent";
import { buildDeviceInformationValues } from "./deviceInformationValues";
import EdgeFirmwareDialogs from "./EdgeFirmwareDialogs";
import EdgeFirmwareVersionRow from "./EdgeFirmwareVersionRow";
import FirmwareUpgradeLogs from "./FirmwareUpgradeLogs";
import useStyles from "./DeviceManagement.style";
import { useEdgeFirmwareOta } from "./useEdgeFirmwareOta";

/** Set true to show the live firmware upgrade log panel again. */
const SHOW_FIRMWARE_UPGRADE_LOGS = false;

export type DeviceCardCompanion = "auto" | "config" | "terminal" | "none";
export type DeviceCardLayout = "row" | "stack";

interface DeviceInformationCardProps {
  device: any;
  setDevice: (device: any) => void;
  deviceId: string;
  history: any;
  companion?: DeviceCardCompanion;
  layout?: DeviceCardLayout;
  compact?: boolean;
  ownOtaLifecycle?: boolean;
  onRetryConnection?: () => any;
  otaRef?: React.MutableRefObject<any>;
  fmStatus?: string;
  fmPercent?: string;
  onFileLoad?: (event: any) => void;
}

const DeviceInformationCard = ({
  device,
  setDevice,
  deviceId,
  history,
  companion = "auto",
  layout = "row",
  compact = false,
  ownOtaLifecycle = true,
  onRetryConnection,
  otaRef,
  fmStatus,
  fmPercent,
  onFileLoad
}: DeviceInformationCardProps) => {
  const styles: any = useStyles();
  const getDeviceById = useStoreActions((actions) => actions.devices.getDeviceById);
  const deleteDevice = useStoreActions((actions) => actions.devices.deleteDevice);
  const controlDevice = useStoreActions((actions) => actions.devices.controlDevice);
  const getDevicesTypesNames = useStoreState((state) => state.getDevicesTypesNames);
  const getSiteName = useStoreState((state) => state.sites.getSiteName);
  const { dateFormat, timeFormat } = useStoreState((state) => state.users);
  const safeDateFormat = typeof dateFormat === "string" ? dateFormat : "MM/DD/YYYY";
  const safeTimeFormat = typeof timeFormat === "string" ? timeFormat : "HH:mm";

  const [selectedDeviceToDelete, setDeviceToDelete] = useState<any>(null);
  const [promptColor, setPromptColor] = useState("#f05347");
  const consoleRef = useRef<HTMLDivElement | null>(null);

  const isNGdevice = device?.deviceType == 1 && device?.type == 3;
  const edgeOta = useEdgeFirmwareOta({
    device: device || {},
    setDevice,
    deviceId,
    firmwareVersion: device?.firmwareVersion,
    isNGdevice
  });

  if (otaRef) {
    otaRef.current = edgeOta;
  }

  useEffect(() => {
    if (!ownOtaLifecycle || !deviceId) {
      return undefined;
    }
    edgeOta.fetchEnrichedDeviceRef.current = async (options = {}) => {
      const res: any = await getDeviceById(deviceId);
      return edgeOta.enrichDevice(res, options);
    };
    if (isNGdevice && device?.id) {
      edgeOta.checkEdgeStatusOnEnter(device);
      edgeOta.enrichDevice(device);
    }
    return () => {
      edgeOta.cleanup();
    };
  }, [ownOtaLifecycle, deviceId, isNGdevice]);

  const deleteSelectedDevice = () => {
    deleteDevice(selectedDeviceToDelete);
    setDeviceToDelete(null);
    history.push("/devices");
  };

  const runDeviceCommand = useCallback((cmd: any, print: any) => {
    const send = async () => {
      setPromptColor("black");
      const command = { command: cmd.slice(0).join(" ") };
      try {
        print(await controlDevice({ deviceId, command }));
      } catch (err: any) {
        print(_.isString(err) ? err : JSON.stringify(err));
      }
      setPromptColor("#f05347");
    };
    // terminal-in-react prints whatever a command returns, so this stays sync
    // and returns nothing — awaiting here would print the pending promise.
    send();
  }, [controlDevice, deviceId]);

  const terminalStyle = useMemo(() => ({
    height: "100%",
    maxHeight: "100%",
    fontSize: "1.125em",
    width: "100%"
  }), []);

  const terminalCommands = useMemo(() => ({
    help: runDeviceCommand
  }), [runDeviceCommand]);

  const terminalConfig = {
    promptColor,
    style: terminalStyle,
    commands: terminalCommands,
    commandPassThrough: runDeviceCommand
  };

  const resolvedCompanion =
    companion === "auto" ? (isNGdevice ? "config" : "terminal") : companion;
  const showTerminal =
    resolvedCompanion === "terminal" &&
    (isNGdevice || device?.protocolVersion >= 1);
  const showConfig = resolvedCompanion === "config";

  useLayoutEffect(() => {
    const root = consoleRef.current;
    if (!root || !showTerminal) {
      return undefined;
    }
    const patchFocus = (el: HTMLElement) => {
      if ((el as any)?.__preventScrollFocus) {
        return;
      }
      (el as any).__preventScrollFocus = true;
      const originalFocus = el.focus.bind(el);
      el.focus = (options?: FocusOptions) => {
        originalFocus({ ...options, preventScroll: true });
      };
    };
    const patchAll = () => {
      root.querySelectorAll("input, textarea, [contenteditable='true']").forEach((node) => {
        patchFocus(node as HTMLElement);
      });
    };
    patchAll();
    const observer = new MutationObserver(patchAll);
    observer.observe(root, { childList: true, subtree: true });

    const blurTerminal = () => {
      const active = document.activeElement as HTMLElement | null;
      if (active && root.contains(active)) {
        active.blur();
      }
    };
    const onWheel = (event: WheelEvent) => {
      const active = document.activeElement as HTMLElement | null;
      if (!active || !root.contains(active)) {
        return;
      }
      blurTerminal();
      const scroller = document.querySelector('[class*="pageContent"]') as HTMLElement | null;
      if (scroller) {
        scroller.scrollTop += event.deltaY;
      }
    };
    window.addEventListener("wheel", onWheel, { passive: true, capture: true });
    window.addEventListener("touchmove", blurTerminal, { passive: true, capture: true });

    return () => {
      observer.disconnect();
      window.removeEventListener("wheel", onWheel, true);
      window.removeEventListener("touchmove", blurTerminal, true);
    };
  }, [showTerminal]);

  const {
    serial,
    isConnected,
    firmwareVersion,
    buildDate,
    disconnectedTimestamp,
    connectedTimestamp
  } = device || {};

  return (
    <>
      <Paper
        className={
          layout === "stack"
            ? styles.sectionStacked
            : compact
            ? styles.sectionCompact
            : styles.section
        }
      >
        <Formik
          initialValues={buildDeviceInformationValues(
            device,
            getDevicesTypesNames,
            safeDateFormat,
            safeTimeFormat
          )}
          enableReinitialize={true}
          onSubmit={() => undefined}
        >
          {(formProps) => (
            <form
              className={
                layout === "stack" ? styles.siteDetailsFormStacked : styles.siteDetailsForm
              }
            >
              <Typography className={styles.deviceInfoTitle}>
                {t`Device Information`}
                <IconButton
                  onClick={() => {
                    setDeviceToDelete(device.id);
                  }}
                >
                  <Delete />
                </IconButton>
              </Typography>
              <AdvancedInputField
                name="serialNumber"
                label={t`Serial Number`}
                editable={false}
                {...formProps}
              />
              <AdvancedInputField
                name="deviceType"
                label={t`Device Type`}
                editable={false}
                {...formProps}
              />
              <AdvancedInputField
                name="connectionStatus"
                label={t`Connection Status`}
                editable={false}
                useReadOnly
                inputClassName={
                  isConnected ? styles.onlineStatusText : styles.offlineStatusText
                }
                {...formProps}
              />
              <AdvancedInputField
                name="protocolVersion"
                label={t`Protocol Version`}
                editable={false}
                {...formProps}
              />
              {firmwareVersion && (
                isNGdevice ? (
                  <EdgeFirmwareVersionRow
                    formikProps={formProps}
                    device={device}
                    isFirmwareUpdating={edgeOta.isFirmwareUpdating}
                    currentFwStage={edgeOta.currentFwStage}
                    currentFwStatus={edgeOta.currentFwStatus}
                    canForceFirmware={edgeOta.canForceFirmware}
                    upgradeActionId={edgeOta.upgradeActionId}
                    isForcingFirmware={edgeOta.isForcingFirmware}
                    isCancellingFirmware={edgeOta.isCancellingFirmware}
                    currentFirmwareCatalog={edgeOta.currentFirmwareCatalog}
                    onForce={edgeOta.handleForceFirmwareUpgrade}
                    onCancel={edgeOta.handleCancelFirmwareUpgrade}
                    onOpenUpgrade={() => edgeOta.setOpenUpgradeDialog(true)}
                  />
                ) : (
                  <AdvancedInputField
                    name="firmwareVersion"
                    label={t`Firmware Version`}
                    editable={false}
                    {...formProps}
                  />
                )
              )}
              {isConnected ? (
                <AdvancedInputField
                  name="connectedTimestamp"
                  label={t`Connected At`}
                  editable={false}
                  {...formProps}
                />
              ) : (
                (disconnectedTimestamp || disconnectedTimestamp === 0) && (
                  <AdvancedInputField
                    name="disconnectedTimestamp"
                    label={t`Last Disconnected At`}
                    editable={false}
                    {...formProps}
                  />
                )
              )}
              {buildDate && (
                <AdvancedInputField
                  name="buildDate"
                  label={t`Build Date`}
                  editable={false}
                  {...formProps}
                />
              )}
              {SHOW_FIRMWARE_UPGRADE_LOGS && isNGdevice && (
                <FirmwareUpgradeLogs logs={edgeOta.firmwareLogs} />
              )}
              {!isNGdevice && onFileLoad && (
                <div className={styles.addButtonContainer}>
                  <Button
                    className={styles.buttonStyle}
                    variant="contained"
                    component="label"
                  >
                    {t`Upload firmware update`}
                    <input
                      onChange={onFileLoad}
                      type="file"
                      style={{ display: "none" }}
                    />
                  </Button>
                </div>
              )}
              {fmStatus && (
                <div>
                  <Typography className={styles.deviceInfoTitle}>{fmStatus}</Typography>
                  <Typography>{fmPercent}</Typography>
                </div>
              )}
            </form>
          )}
        </Formik>
        <Grid
          className={layout === "stack" ? styles.consoleContainerStacked : styles.consoleContainer}
        >
          <div ref={consoleRef} className={styles.consoleSurface}>
          {showTerminal && (
            <Terminal
              key={`device-terminal-${deviceId}`}
              startState="maximised"
              color="#f05347"
              outputColor="white"
              prompt={promptColor}
              backgroundColor="black"
              barColor="black"
              style={terminalStyle}
              commands={terminalCommands}
              commandPassThrough={runDeviceCommand}
              msg="DEVICE CONTROL"
              allowTabs={false}
              showActions={false}
            />
          )}
          {showConfig && (
            <ConfigComponent
              terminalConfig={terminalConfig}
              deviceId={device.id}
              isConnected={isConnected}
              onRetryConnection={onRetryConnection}
            />
          )}
          </div>
        </Grid>
      </Paper>
      <SimpleModal
        showModal={!!selectedDeviceToDelete}
        title={t`Are you sure?`}
        contentText={
          device.site
            ? t`Are you sure you want to delete device` +
              ` ${serial} ` +
              t`in site ` +
              `${getSiteName(device.site)}` +
              ` ?`
            : t`Are you sure you want to delete device ` + `${serial} ?`
        }
        actionName={t`OK`}
        onAction={deleteSelectedDevice}
        closeModal={() => setDeviceToDelete(null)}
      />
      <EdgeFirmwareDialogs
        open={isNGdevice}
        firmwareVersion={firmwareVersion}
        edgeOta={edgeOta}
      />
    </>
  );
};

export default DeviceInformationCard;
